/**
 * AZ list: hover the period month name to show the AZ saldo infobox flyout.
 * PDF icons keep their own reveal; this trigger is only .js-az-saldo-hover.
 */

const SHOW_DELAY_MS = 350;
const CLOSE_DELAY_MS = 180;

let panel = null;
let showTimer = null;
let closeTimer = null;
let activeTrigger = null;
let hoverToken = 0;
let openedByClick = false;

function prefersFineHover() {
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

function cancelTimers() {
  if (showTimer) {
    clearTimeout(showTimer);
    showTimer = null;
  }
  if (closeTimer) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }
}

function ensurePanel() {
  if (panel) return panel;
  panel = document.createElement('div');
  panel.className = 'az-saldo-hover-card';
  panel.setAttribute('role', 'tooltip');
  panel.hidden = true;
  document.body.appendChild(panel);
  panel.addEventListener('mouseenter', () => {
    cancelTimers();
  });
  panel.addEventListener('mouseleave', (event) => {
    if (openedByClick) return;
    const related = event.relatedTarget;
    if (related && triggerFromElement(related)) return;
    scheduleClose();
  });
  return panel;
}

function triggerFromElement(node) {
  if (!node || !node.closest) return null;
  if (node.closest('.az-saldo-hover-card')) return null;
  if (node.closest('.pdf_trigger, [data-pdf-url], [id^="az_pdf_cell_"]')) return null;
  const trigger = node.closest('.js-az-saldo-hover');
  if (!trigger) return null;
  const sourceId = trigger.getAttribute('data-az-saldo-source');
  if (!sourceId) return null;
  const source = document.getElementById(sourceId);
  if (!source) return null;
  return { trigger, source };
}

function positionPanel(trigger) {
  if (!panel || !trigger) return;
  const rect = trigger.getBoundingClientRect();
  const margin = 8;
  const gap = 6;
  const panelWidth = panel.offsetWidth;
  const panelHeight = panel.offsetHeight;
  let top = rect.bottom + gap;
  let left = rect.left;

  if (top + panelHeight > window.innerHeight - margin) {
    top = rect.top - panelHeight - gap;
    panel.style.transformOrigin = 'bottom left';
  } else {
    panel.style.transformOrigin = 'top left';
  }
  if (left + panelWidth > window.innerWidth - margin) {
    left = window.innerWidth - panelWidth - margin;
  }
  if (left < margin) left = margin;
  if (top < margin) top = margin;

  panel.style.top = `${Math.round(top)}px`;
  panel.style.left = `${Math.round(left)}px`;
}

function hidePanel() {
  if (!panel) return;
  panel.classList.remove('is-open');
  panel.hidden = true;
  panel.innerHTML = '';
  activeTrigger = null;
  openedByClick = false;
}

function showPanel(info) {
  const card = ensurePanel();
  card.innerHTML = info.source.innerHTML;
  card.hidden = false;
  card.classList.remove('is-open');
  positionPanel(info.trigger);
  requestAnimationFrame(() => {
    card.classList.add('is-open');
  });
  activeTrigger = info.trigger;
}

function scheduleOpen(info) {
  cancelTimers();
  if (activeTrigger === info.trigger && panel && !panel.hidden && panel.classList.contains('is-open')) {
    return;
  }

  const token = ++hoverToken;
  showTimer = setTimeout(() => {
    showTimer = null;
    if (token !== hoverToken) return;
    showPanel(info);
  }, SHOW_DELAY_MS);
}

function scheduleClose() {
  if (closeTimer) clearTimeout(closeTimer);
  closeTimer = setTimeout(() => {
    closeTimer = null;
    hoverToken += 1;
    hidePanel();
  }, CLOSE_DELAY_MS);
}

function onDocumentOver(event) {
  if (!prefersFineHover()) return;
  if (event.target.closest && event.target.closest('.az-saldo-hover-card')) {
    cancelTimers();
    return;
  }
  const info = triggerFromElement(event.target);
  if (!info) return;
  cancelTimers();
  scheduleOpen(info);
}

function onDocumentOut(event) {
  if (openedByClick) return;
  if (!prefersFineHover()) return;
  const related = event.relatedTarget;
  if (related && related.closest) {
    if (related.closest('.az-saldo-hover-card')) return;
    if (triggerFromElement(related)) return;
  }
  const leaving = triggerFromElement(event.target);
  const leavingCard = event.target && event.target.closest && event.target.closest('.az-saldo-hover-card');
  if (!leaving && !leavingCard) return;
  cancelTimers();
  scheduleClose();
}

function togglePanel(info) {
  cancelTimers();
  if (activeTrigger === info.trigger && panel && !panel.hidden) {
    hoverToken += 1;
    hidePanel();
    return;
  }
  hoverToken += 1;
  openedByClick = true;
  showPanel(info);
}

function onDocumentClick(event) {
  const info = triggerFromElement(event.target);
  if (info) {
    if (!prefersFineHover()) {
      event.preventDefault();
      togglePanel(info);
    }
    return;
  }
  if (!panel || panel.hidden) return;
  if (event.target.closest && event.target.closest('.az-saldo-hover-card')) return;
  hoverToken += 1;
  hidePanel();
}

function onDocumentFocusIn(event) {
  const info = triggerFromElement(event.target);
  if (!info) return;
  cancelTimers();
  openedByClick = true;
  hoverToken += 1;
  showPanel(info);
}

function onDocumentFocusOut(event) {
  const related = event.relatedTarget;
  if (related && related.closest && related.closest('.az-saldo-hover-card')) return;
  if (related && triggerFromElement(related)) return;
  const leaving = triggerFromElement(event.target);
  if (!leaving) return;
  scheduleClose();
}

export function initAzSaldoHover() {
  if (document.documentElement.dataset.azSaldoHoverReady === 'true') return;
  document.documentElement.dataset.azSaldoHoverReady = 'true';
  document.addEventListener('mouseover', onDocumentOver);
  document.addEventListener('mouseout', onDocumentOut);
  document.addEventListener('click', onDocumentClick);
  document.addEventListener('focusin', onDocumentFocusIn);
  document.addEventListener('focusout', onDocumentFocusOut);
  window.addEventListener('scroll', () => {
    if (panel && !panel.hidden) {
      hoverToken += 1;
      hidePanel();
    }
  }, { passive: true });
  window.addEventListener('resize', () => {
    if (panel && !panel.hidden) {
      hoverToken += 1;
      hidePanel();
    }
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && panel && !panel.hidden) {
      hoverToken += 1;
      hidePanel();
    }
  });
}
